import type { StatusTone } from '../contracts/common';
import type {
  AssetComponentKind,
  AssetStatus,
  AssetType,
  ReminderAudience,
  ReminderChannel,
  ReminderTargetType,
  RenewalPaymentStatus,
} from '../contracts/maintenance';

/** Typ zasobu (Figma „Dodaj zasób”: Strona, Aplikacja, Domena, Skrzynka, Inne). */
export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  website: 'Strona',
  app: 'Aplikacja',
  domain: 'Domena',
  mailbox: 'Skrzynka',
  server: 'Serwer',
  other: 'Inne',
};

/** Podpis pod nazwą w liście zasobów (Figma 08: „strona www”, „aplikacja webowa”). */
export const ASSET_TYPE_SUBTITLES: Record<AssetType, string> = {
  website: 'strona www',
  app: 'aplikacja webowa',
  domain: 'domena',
  mailbox: 'skrzynka',
  server: 'serwer',
  other: 'inne',
};

/** Zakładki listy zasobów (Figma 08: Wszystko, Strony, Aplikacje, Domeny, Skrzynki). */
export const ASSET_TYPE_PLURAL_LABELS: Record<AssetType, string> = {
  website: 'Strony',
  app: 'Aplikacje',
  domain: 'Domeny',
  mailbox: 'Skrzynki',
  server: 'Serwery',
  other: 'Inne',
};

export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  ok: 'Działa',
  alarm: 'Alarm',
  active: 'Aktywna',
  disabled: 'Wyłączona',
  expired: 'Wygasła',
};

export const ASSET_STATUS_TONES: Record<AssetStatus, StatusTone> = {
  ok: 'success',
  alarm: 'danger',
  active: 'neutral',
  disabled: 'neutral',
  expired: 'danger',
};

export const ASSET_COMPONENT_KIND_LABELS: Record<AssetComponentKind, string> = {
  hosting: 'Hosting',
  domain: 'Domena',
  ssl: 'Certyfikat SSL',
  database: 'Baza danych',
  mailbox: 'Skrzynka',
  other: 'Inne',
};

export const RENEWAL_PAYMENT_STATUS_LABELS: Record<RenewalPaymentStatus, string> = {
  pending: 'Oczekuje',
  invoiced: 'Zafakturowana',
  paid: 'Opłacona',
  overdue: 'Po terminie',
};

export const RENEWAL_PAYMENT_STATUS_TONES: Record<RenewalPaymentStatus, StatusTone> = {
  pending: 'warning',
  invoiced: 'accent',
  paid: 'success',
  overdue: 'danger',
};

export const REMINDER_TARGET_TYPE_LABELS: Record<ReminderTargetType, string> = {
  service: 'Umowa',
  supplier_renewal: 'Koszt dostawcy',
};

export const REMINDER_AUDIENCE_LABELS: Record<ReminderAudience, string> = {
  owner: 'Do Ciebie',
  client: 'Do klienta',
};

export const REMINDER_CHANNEL_LABELS: Record<ReminderChannel, string> = {
  today: 'Dziś',
  inbox: 'Inbox',
  push: 'Powiadomienie push',
  email_approval: 'Mail przez Akceptacje',
};

function joinDays(days: number[]): string {
  if (days.length === 1) return String(days[0]);
  return `${days.slice(0, -1).join(', ')} i ${days[days.length - 1]}`;
}

/**
 * Opis reguły przypomnień do UI (Figma: karta zasobu → Przypomnienia), np. „Do Ciebie: 30 i 7 dni przed”,
 * „Do klienta: 30 dni przed, mail przez Akceptacje”, „Po terminie: co 7 dni, najwyżej 3 razy”.
 */
export function describeReminderRule(rule: {
  audience: ReminderAudience;
  offsetsDays: readonly number[];
  channel: ReminderChannel;
  maxRepeats?: number | null;
}): string {
  const before = rule.offsetsDays
    .filter((d) => d <= 0)
    .map((d) => -d)
    .sort((a, b) => b - a);
  const after = rule.offsetsDays.filter((d) => d > 0).sort((a, b) => a - b);
  const parts: string[] = [];
  if (before.length > 0) {
    parts.push(before.length === 1 && before[0] === 0 ? 'w dniu terminu' : `${joinDays(before)} dni przed`);
  }
  if (after.length > 0) {
    const step = after[0] as number;
    const regular = after.every((d, i) => d === step * (i + 1));
    const repeats = rule.maxRepeats ?? after.length;
    parts.push(
      regular && after.length > 1
        ? `co ${step} dni, najwyżej ${Math.min(repeats, after.length)} ${repeats === 1 ? 'raz' : 'razy'}`
        : `${joinDays(after)} dni po terminie`,
    );
  }
  const who = after.length > 0 && before.length === 0 ? 'Po terminie' : REMINDER_AUDIENCE_LABELS[rule.audience];
  const channel = rule.channel === 'email_approval' ? ', mail przez Akceptacje' : '';
  const prefix = after.length > 0 && before.length === 0 && rule.audience === 'client' ? 'Do klienta po terminie' : who;
  return `${prefix}: ${parts.join(', ')}${channel}`;
}
